import React, { useState, useEffect } from 'react';
import { CustomDropdown } from '../common/CustomDropdown.js';
import { BugSourceEnum } from '../enums/IssueEnums.js';
import '../common/modals.css';

// Convert BugSourceEnum to options format
const BUG_SOURCE_OPTIONS = Object.values(BugSourceEnum).map(source => ({
  value: source.value,
  display: source.displayName
}));

// Function to get bug source based on view controller name
const getBugSourceFromView = (viewControllerName) => {
  if (!viewControllerName) return 'USER_VIEW'; // Default fallback
  
  const viewName = viewControllerName.toLowerCase();
  if (viewName.includes('document') && viewName.includes('detail')) {
    return 'DOCS_DETAIL_VIEW';
  } else if (viewName.includes('document')) {
    return 'DOCS_LIST_VIEW';
  } else if (viewName.includes('outcome') || viewName.includes('task')) {
    return 'OUTCOME_TASKS_VIEW';
  } else if (viewName.includes('admin')) {
    return 'ADMIN_VIEW';
  } else if (viewName.includes('insights')) {
    return 'INSIGHTS_VIEW';
  } else if (viewName.includes('clinic')) {
    return 'CLINIC_SELECT_VIEW';
  } else if (viewName.includes('config')) {
    return 'CONFIG_VIEW';
  } else {
    return 'USER_VIEW'; // Default for unknown views
  }
};

// Helper function to get default source display name
const getDefaultSourceDisplay = (viewControllerName) => {
  const detectedSource = getBugSourceFromView(viewControllerName);
  const option = BUG_SOURCE_OPTIONS.find(option => option.value === detectedSource);
  return option ? option.display : 'User View';
};

const BugReportModal = ({ isOpen, onClose, onSubmit, viewControllerName = 'User View', contextInfo = '' }) => {
  const [formData, setFormData] = useState({
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(getDefaultSourceDisplay(viewControllerName));

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        description: ''
      });
      setErrors({});
      setIsSubmitting(false);
      
      // Reset dropdown to detected source for current view
      setSelectedSource(getDefaultSourceDisplay(viewControllerName));
      setIsDropdownOpen(false);
    }
  }, [isOpen, viewControllerName]);

  // Toggle dropdown function
  const toggleSourceDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleBugSourceSelect = (option) => {
    setSelectedSource(option.display);
    setIsDropdownOpen(false);
    
    // Clear error when user selects
    if (errors.source) {
      setErrors(prev => ({ ...prev, source: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!selectedSource || selectedSource === 'Select source...') {
      newErrors.source = 'Please select a source';
    }
    
    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Find the selected option to get its value
      const selectedOption = BUG_SOURCE_OPTIONS.find(option => option.display === selectedSource);
      const sourceValue = selectedOption ? selectedOption.value : getBugSourceFromView(viewControllerName);
      
      // Combine contextInfo with user's description (contextInfo first, then user's description)
      const fullDescription = contextInfo ? `${contextInfo}${formData.description.trim()}` : formData.description.trim();
      
      const bugData = {
        type: 'BUG',
        source: sourceValue,
        description: fullDescription
      };
      
      await onSubmit(bugData);
      onClose();
    } catch (error) {
      console.error('Error submitting problem report:', error);
      setErrors({ submit: error.message || 'Failed to submit problem report' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-container modal-animated" style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>Report a Problem</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modal-content">
          <form onSubmit={handleSubmit} style={{ margin: 0 }}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500', 
              color: '#374151', 
              fontSize: '14px' 
            }}>Source *</label>
            <CustomDropdown
              selected={selectedSource}
              options={BUG_SOURCE_OPTIONS}
              isOpen={isDropdownOpen}
              toggleDropdown={toggleSourceDropdown}
              onSelect={handleBugSourceSelect}
              disabled={isSubmitting}
            />
            {errors.source && <span style={{ 
              display: 'block', 
              color: '#ef4444', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>{errors.source}</span>}
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="description" style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500', 
              color: '#374151', 
              fontSize: '14px' 
            }}>Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Please describe the bug in detail..."
              disabled={isSubmitting}
              rows={6}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: errors.description ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                resize: 'vertical',
                minHeight: '120px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{ 
              display: 'block', 
              color: '#6b7280', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>
              {formData.description.length} characters (minimum 10 required)
            </small>
            {errors.description && <span style={{ 
              display: 'block', 
              color: '#ef4444', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>{errors.description}</span>}
          </div>

          {errors.submit && (
            <div style={{ 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca', 
              color: '#dc2626', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px', 
              fontSize: '14px' 
            }}>
              {errors.submit}
            </div>
          )}

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#2563eb')}
              onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#3b82f6')}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Problem Report'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Feature Request Modal Component
const FeatureRequestModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        description: ''
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.description || formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const featureData = {
        type: 'FEATURE',
        source: null, // Explicitly set null for feature requests (BugSource only applies to bugs)
        description: formData.description.trim()
      };
      
      await onSubmit(featureData);
      onClose();
    } catch (error) {
      console.error('Error submitting feature request:', error);
      setErrors({ submit: error.message || 'Failed to submit feature request' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-container modal-animated" style={{ maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>Suggest a Feature</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            ×
          </button>
        </div>
        
        <div className="modal-content">
          <form onSubmit={handleSubmit} style={{ margin: 0 }}>
          <div style={{ marginBottom: '20px' }}>
            <label htmlFor="description" style={{ 
              display: 'block', 
              marginBottom: '6px', 
              fontWeight: '500', 
              color: '#374151', 
              fontSize: '14px' 
            }}>Feature Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Please describe the feature you'd like to see..."
              disabled={isSubmitting}
              rows={8}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: errors.description ? '1px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box',
                resize: 'vertical',
                minHeight: '150px',
                fontFamily: 'inherit'
              }}
            />
            <small style={{ 
              display: 'block', 
              color: '#6b7280', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>
              {formData.description.length} characters (minimum 10 required)
            </small>
            {errors.description && <span style={{ 
              display: 'block', 
              color: '#ef4444', 
              fontSize: '12px', 
              marginTop: '4px' 
            }}>{errors.description}</span>}
          </div>

          {errors.submit && (
            <div style={{ 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca', 
              color: '#dc2626', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '20px', 
              fontSize: '14px' 
            }}>
              {errors.submit}
            </div>
          )}

          <div className="modal-actions">
            <button 
              type="button" 
              onClick={onClose}
              disabled={isSubmitting}
              className="cancel-button"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              disabled={isSubmitting}
              style={{
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
              onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#059669')}
              onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#10b981')}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feature Request'}
            </button>
          </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BugReportModal;
export { FeatureRequestModal };
